import { ctxMap } from '@/contexts/MapContext';
import { ColorDto, FeatureCollectionDto, FeatureDto } from '@/generated/model';
import { useContext, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { Layer, LeafletMouseEvent, Tooltip } from 'leaflet';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import useGois from '@/hooks/useGois';
import L from 'leaflet';

import 'leaflet.vectorgrid';

export default function GoiVisualizer() {
  const mapCtx = useContext(ctxMap);
  const gois = useGois(mapCtx?.selectedGoiGroup);
  const map = useMap();
  const layersRef = useRef<Layer[]>([]);
  const featureCenters: Record<number, L.LatLng> = {};
  let tooltip: Tooltip | null = null;

  // vectorGrid bug workaround
  function patchVectorGridLayer(obj: any) {
    obj._createLayer_orig = obj._createLayer;
    obj._createLayer = function (feat: any, pxPerExtent: any, layerStyle: any) {
      const layer = this._createLayer_orig(feat, pxPerExtent, layerStyle);
      if (feat.type === 1) {
        layer.getLatLng = null;
      }
      return layer;
    };
    return obj;
  }

  useEffect(() => {
    if (!gois) {
      return;
    }
    const layers = layersRef.current;
    if (layers.length) {
      layers.forEach((layer) => layer.remove());
    }
    gois.forEach((goi) => {
      if (!isPhoneOrHighScreen()) {
        setCenter(goi, featureCenters);
      }
      const layer = patchVectorGridLayer(
        L.vectorGrid.slicer(goi, {
          interactive: true,
          getFeatureId: (feature: FeatureDto) => feature.properties.id,
          maxZoom: 30,
          buffer: 128,
          vectorTileLayerStyles: {
            sliced: (_: Record<string, any>, zoom: number) => {
              return {
                fill: true,
                fillColor: toHexColor(goi.color),
                fillOpacity: zoom >= 10 ? 0.5 : 0.2,
                color: 'black',
                opacity: zoom > 14 ? 0.3 : zoom > 7 ? 0.4 : 0.7,
                weight: zoom < 8 ? 3 : zoom < 12 ? 2 : 1,
                radius: 5,
              };
            },
          },
        })
      );
      layers.push(layer);
      const vectorGrid = layer.addTo(map);
      addHoverEvent(vectorGrid, goi);
      addClickEvent(vectorGrid);
    });
    return () => {
      if (layersRef.current.length) {
        layersRef.current.forEach((layer) => layer.remove());
      }
    };
  }, [map, gois]);

  function addHoverEvent(vectorGrid: any, goi: FeatureCollectionDto) {
    if (isPhoneOrHighScreen()) {
      return;
    }
    vectorGrid.on('mouseover', function (e: LeafletMouseEvent) {
      const featureId = e.propagatedFrom.properties.id;
      vectorGrid.setFeatureStyle(featureId, {
        fill: true,
        fillColor: brightenColor(goi.color),
        fillOpacity: 0.4,
        color: 'black',
        weight: 1,
        radius: 5,
      });

      const name = e.propagatedFrom.properties.name;
      const center = featureCenters[featureId];
      if (tooltip) {
        if (tooltip.getContent() == name) {
          return;
        }
        map.removeLayer(tooltip);
        tooltip = null;
      }
      tooltip = L.tooltip({
        permanent: false,
        direction: 'top',
      })
        .setLatLng(center)
        .setContent(name)
        .addTo(map);
    });

    vectorGrid.on('mouseout', function (e: LeafletMouseEvent) {
      const target = e.originalEvent.relatedTarget;
      if (target && (target as any).tagName != 'DIV') {
        return;
      }
      const featureId = e.propagatedFrom.properties.id;
      vectorGrid.resetFeatureStyle(featureId);
      if (tooltip) {
        map.removeLayer(tooltip);
        tooltip = null;
      }
    });
  }

  function addClickEvent(vectorGrid: any) {
    if (!isPhoneOrHighScreen()) {
      return;
    }

    vectorGrid.on('click', (e: any) => {
      const name = e.propagatedFrom.properties.name;
      tooltip = L.tooltip({
        direction: 'top',
        permanent: false,
      })
        .setLatLng(e.latlng)
        .setContent(name)
        .addTo(map);
    });
  }

  function brightenColor(color: ColorDto, percent = 30) {
    let r = color.red + percent;
    let g = color.green + percent;
    let b = color.blue + percent;

    r = Math.min(255, r);
    g = Math.min(255, g);
    b = Math.min(255, b);

    return '#' + (b | (g << 8) | (r << 16)).toString(16).padStart(6, '0');
  }

  function toHexColor(color: ColorDto) {
    const r = color.red;
    const g = color.green;
    const b = color.blue;
    return '#' + (b | (g << 8) | (r << 16)).toString(16).padStart(6, '0');
  }

  function setCenter(featureCollection: FeatureCollectionDto, featureCenters: Record<number, L.LatLng>) {
    featureCollection.features.forEach((feature) => {
      const center = L.geoJSON(feature).getBounds().getCenter();
      const id = feature.id;
      featureCenters[id] = center;
    });
  }

  return <></>;
}
